import React, { useEffect } from 'react';
import { Col, Row, Form, Card } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
// icons
import { IoCloseOutline } from 'react-icons/io5';
//constants
import { getLocationWeb, getContentProduct } from 'constants';
//helpers
import { verificateNullValue } from 'helpers';
import { useListsStore } from 'stores';
// components
import Button from 'components/backoffice/buttonCustom';
import CustomInput from 'components/backoffice/customInput';
import DeleteModalCustom from 'components/backoffice/modals/DeleteModalCustom';
import CustomCheckbox from 'components/backoffice/customCheckbox';
import { SelectInputCustom } from 'components/input/index.js';
import SearchProduct from 'components/backoffice/customInput/SearchProduct';
import SpinnerComponent from 'components/backoffice/SpinnerComponent';
// images
import DefaultImage from 'assets/img/preview.jpg';

const ListForm = ({
  isEditing,
  onSubmitForm,
  onDelete,
  openModalDelete,
  setOpenModalDelete,
  defaultValues,
  loading,
}) => {
  const { t } = useTranslation(['backoffice', 'header']);
  const { types } = useListsStore();

  const {
    formState: { errors },
    setValue,
    reset,
    control,
    handleSubmit,
    watch,
  } = useForm();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'products',
  });

  const watchedFields = watch();

  const contenidos = getContentProduct(t);
  const locationWeb = getLocationWeb(t);

  const handleOnDragEnd = (result) => {
    if (!result.destination) return;
    const items = watchedFields.products;
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setValue('products', items);
  };

  const onAddProduct = (product) => {
    if (product !== null && !fields.some((prod) => prod.value === product.value)) {
      append(product);
    }
  };

  useEffect(() => {
    if (!isEditing) reset(defaultValues);
  }, [reset, defaultValues]);

  useEffect(() => {
    if (defaultValues) {
      setValue('nombre', verificateNullValue(defaultValues?.nombre));
      setValue('location_web', defaultValues?.location_web_id);
      setValue('tipo', defaultValues?.type_id);
      setValue('order', defaultValues?.order ?? '');
      setValue('estado', defaultValues?.estado);
      setValue('contenido', defaultValues?.contenido_id);
      setValue('nombre_fr', verificateNullValue(defaultValues?.nombre_fr));
      setValue('nombre_ca', verificateNullValue(defaultValues?.nombre_ca));
      setValue('nombre_eu', verificateNullValue(defaultValues?.nombre_eu));
      setValue('nombre_en', verificateNullValue(defaultValues?.nombre_en));
      setValue('nombre_gl', verificateNullValue(defaultValues?.nombre_gl));
      if (defaultValues.products) {
        const customProducts = defaultValues.products.map((prod) => {
          return {
            id: prod.id,
            ean: prod.ean,
            title: prod.title,
            image: prod.image,
            label: `${prod.title} (${prod.ean})`,
            value: prod.id,
            publicada: prod.publicada,
          };
        });
        setValue('products', customProducts);
      }
    }
  }, [defaultValues]);

  return (
    <div className="mt-5">
      <Form onSubmit={handleSubmit(onSubmitForm)}>
        <Row className="mt-4">
          <Col sm={12} md={12} lg={4} className="mb-md-2 mb-lg-0">
            <Controller
              name="nombre"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('camporequerido'),
                },
              }}
              render={({ field: { onChange, value } }) => (
                <CustomInput
                  value={value}
                  type="text"
                  label={t('nombre')}
                  placeholder={t('name')}
                  onChange={(e) => onChange(e)}
                />
              )}
            />
            {errors?.nombre && <small className="text-danger ">{errors.nombre.message}</small>}
          </Col>
          <Col sm={12} md={12} lg={4} className="mb-md-2 mb-lg-0">
            <Controller
              name="nombre_en"
              control={control}
              render={({ field: { onChange, value } }) => (
                <CustomInput
                  value={value}
                  type="text"
                  label={`${t('nombre')} ${t('header:english')}`}
                  placeholder={`${t('name')} ${t('header:english')}`}
                  onChange={(e) => onChange(e)}
                />
              )}
            />
            {errors?.nombre_en && <small className="text-danger ">{errors.nombre_en.message}</small>}
          </Col>
          <Col sm={12} md={12} lg={4} className="mt-4 mt-sm-0 mb-md-2 mb-lg-0">
            <Controller
              name="nombre_gl"
              control={control}
              render={({ field: { onChange, value } }) => (
                <CustomInput
                  value={value}
                  type="text"
                  label={`${t('nombre')} ${t('header:galician')}`}
                  placeholder={`${t('name')} ${t('header:galician')}`}
                  onChange={(e) => onChange(e)}
                />
              )}
            />
            {errors?.nombre_gl && <small className="text-danger ">{errors.nombre_gl.message}</small>}
          </Col>
        </Row>
        <Row className="mt-4">
          <Col sm={12} md={12} lg={4} className="mb-md-2 mb-lg-0">
            <Controller
              name="nombre_fr"
              control={control}
              render={({ field: { onChange, value } }) => (
                <CustomInput
                  value={value}
                  type="text"
                  label={`${t('nombre')} ${t('header:frances')}`}
                  placeholder={`${t('name')} ${t('header:frances')}`}
                  onChange={(e) => onChange(e)}
                />
              )}
            />
            {errors?.nombre_fr && <small className="text-danger ">{errors.nombre_fr.message}</small>}
          </Col>
          <Col sm={12} md={12} lg={4} className="mt-4 mt-sm-0 mb-md-2 mb-lg-0">
            <Controller
              name="nombre_ca"
              control={control}
              render={({ field: { onChange, value } }) => (
                <CustomInput
                  value={value}
                  type="text"
                  label={`${t('nombre')} ${t('header:catalan')}`}
                  placeholder={`${t('name')} ${t('header:catalan')}`}
                  onChange={(e) => onChange(e)}
                />
              )}
            />
            {errors?.nombre_ca && <small className="text-danger ">{errors.nombre_ca.message}</small>}
          </Col>
          <Col sm={12} md={12} lg={4} className="mt-4 mt-sm-0 mb-md-2 mb-lg-0">
            <Controller
              name="nombre_eu"
              control={control}
              render={({ field: { onChange, value } }) => (
                <CustomInput
                  value={value}
                  type="text"
                  label={`${t('nombre')} ${t('header:euskera')}`}
                  placeholder={`${t('name')} ${t('header:euskera')}`}
                  onChange={(e) => onChange(e)}
                />
              )}
            />
            {errors?.nombre_eu && <small className="text-danger ">{errors.nombre_eu.message}</small>}
          </Col>
        </Row>
        <Row className="mt-4">
          <Col sm={12} md={12} lg={4} className="mt-4 mt-sm-0 mb-md-2 mb-lg-0">
            <Controller
              name="location_web"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('camporequerido'),
                },
              }}
              render={({ field: { onChange, value } }) => (
                <SelectInputCustom
                  choices={locationWeb}
                  label={t('location')}
                  placeholder={t('select')}
                  value={value}
                  onCheckedItem={onChange}
                />
              )}
            />
            {errors?.location_web && <small className="text-danger ">{errors.location_web.message}</small>}
          </Col>
          <Col sm={12} md={12} lg={4} className="mt-4 mt-sm-0 mb-md-2 mb-lg-0">
            <Controller
              name="tipo"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('camporequerido'),
                },
              }}
              render={({ field: { onChange, value } }) => (
                <SelectInputCustom
                  choices={types}
                  label={t('tipo')}
                  placeholder={t('select')}
                  value={value}
                  onCheckedItem={onChange}
                />
              )}
            />
            {errors?.tipo && <small className="text-danger ">{errors.tipo.message}</small>}
          </Col>
          <Col sm={12} md={12} lg={4} className="mt-4 mt-sm-0 mb-md-2 mb-lg-0">
            <Controller
              name="order"
              control={control}
              render={({ field: { onChange, value } }) => (
                <CustomInput
                  value={value ?? ''}
                  type="number"
                  min={0}
                  label={t('faqs.order')}
                  placeholder="0, 1, 2, 3..."
                  onChange={(val) => onChange(val !== '' && val != null ? parseInt(val, 10) : '')}
                />
              )}
            />
          </Col>
        </Row>
        <Row className="mt-4">
          <Col sm={12} md={12} lg={4} className="mb-md-2 mb-lg-0">
            <Controller
              name="contenido"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('camporequerido'),
                },
              }}
              render={({ field: { onChange, value } }) => (
                <SelectInputCustom
                  choices={contenidos}
                  label={t('contenido')}
                  placeholder={t('select')}
                  value={value}
                  onCheckedItem={onChange}
                />
              )}
            />
            {errors?.contenido && <small className="text-danger ">{errors.contenido.message}</small>}
          </Col>
          <Col sm={12} md={12} lg={4} className="mt-4 mt-sm-0 mb-md-2 mb-lg-0">
            <Controller
              name="estado"
              control={control}
              render={({ field }) => (
                <CustomCheckbox
                  label={t('activo')}
                  checked={field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                />
              )}
            />
          </Col>
        </Row>
        <Row className="mt-4">
          <Col sm={12} md={12} lg={12} className="mb-md-2 mb-lg-0">
            <Row>
              <Col sm={12} md={12} lg={12}>
                <Form.Label className="text-light">{t('seleccionarproducto')}</Form.Label>
                <SearchProduct
                  filterBy="search"
                  onSelected={onAddProduct}
                  labelShow="ean"
                  placeholder={t('searchByEanTitle')}
                />
              </Col>
              <Col sm={12} md={12} lg={12}>
                {fields.length > 0 && (
                  <div className="mt-4 text-muted">
                    <strong className="text-light">{t('listNotVisible')}</strong>
                  </div>
                )}
              </Col>
              <DragDropContext onDragEnd={handleOnDragEnd}>
                <Droppable droppableId="droppable" direction="vertical">
                  {(provided) => (
                    <div {...provided.droppableProps} ref={provided.innerRef} className="mt-4 container-list">
                      {fields.map((list, index) => {
                        return (
                          <Draggable key={list.id} draggableId={list.id.toString()} index={index}>
                            {(provided, snapshot) => (
                              <Card
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                style={{
                                  width: snapshot.isDragging ? '100%' : null,
                                  background: snapshot.isDragging ? '#dee2e6' : null,
                                  display: snapshot.isDragging ? 'table' : null,
                                  ...provided.draggableProps.style,
                                }}
                                className="mt-3"
                              >
                                <Card.Body className={`d-flex ${!list.publicada ? 'bg-custom-gray' : ''}`}>
                                  <div className="d-flex col-3">
                                    <img src={list.image !== null ? list.image : DefaultImage} width="70" height="70" />
                                    <h5 className="mx-3">
                                      {list.title}
                                      <div className="small text-muted mt-1">
                                        {list.publicada ? t('publicado') : t('noPublicado')}
                                      </div>
                                    </h5>
                                  </div>
                                  <div className="col-9 d-flex justify-content-end">
                                    <IoCloseOutline size="30" onClick={() => remove(index)} />
                                  </div>
                                </Card.Body>
                              </Card>
                            )}
                          </Draggable>
                        );
                      })}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </Row>
          </Col>
        </Row>
      </Form>
      <div className="my-4 d-flex justify-content-end gap-3">
        <Button color="primary" onClick={handleSubmit(onSubmitForm)} loading={loading}>
          {t('save')}
          <SpinnerComponent loading={loading} />
        </Button>
        {isEditing && (
          <Button color="secondary" onClick={() => setOpenModalDelete(true)} loading={loading}>
            {t('eliminar')}
          </Button>
        )}
      </div>
      <DeleteModalCustom
        openModal={openModalDelete}
        setOpenModal={setOpenModalDelete}
        handleDelete={onDelete}
        deleteTitle={t('deletelist')}
        beforeTitle={t('edit', { title: defaultValues?.nombre })}
      />
    </div>
  );
};

ListForm.propTypes = {
  onSubmitForm: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  openModalDelete: PropTypes.bool,
  setOpenModalDelete: PropTypes.func,
  defaultValues: PropTypes.object,
  isEditing: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
};

export default ListForm;
